import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { filterAuthorities } from '../../utils/fuzzyMatch';

export interface QrAuthority {
	id: number;
	name: string;
	requiredFields: string[];
	// Admin-assigned search aliases (e.g. "plumber, tap, leak"), matched
	// alongside `name` by fuzzyMatch so a symptom-style query still finds the
	// right authority. See Authority.keywords on the backend.
	keywords?: string;
}

interface Props {
	authorities: QrAuthority[];
	selectedId: number | null;
	onSelect: (id: number) => void;
	hasError?: boolean;
}

/**
 * One field for choosing an authority: type in it to filter, or tap the arrow
 * to browse the full list without bringing up the keyboard. Starts closed.
 * Replaces the browser's native <select> (a full-screen system sheet on
 * Android). Shows names only: Authority.description is a leftover column from
 * the 2024 backend that no admin screen can set, so it could only be stale.
 */
const AuthorityPicker = ({ authorities, selectedId, onSelect, hasError }: Props) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const rootRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listId = useId();
	const selected = authorities.find((a) => a.id === selectedId) || null;

	const filtered = useMemo(
		() =>
			filterAuthorities(
				authorities,
				query,
				(a) => a.name,
				(a) => a.keywords
			),
		[authorities, query]
	);

	const close = () => {
		setOpen(false);
		setQuery('');
		inputRef.current?.blur(); // closing the list also drops the keyboard
	};

	const openList = () => {
		setOpen(true);
		// Bring the field to the top (below the fixed bar) so the list has room,
		// even with the phone keyboard up.
		window.setTimeout(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
	};

	useEffect(() => {
		if (!open) return;
		const onDown = (e: PointerEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
		};
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
		document.addEventListener('pointerdown', onDown);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('pointerdown', onDown);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const choose = (id: number) => {
		onSelect(id);
		close();
	};

	return (
		<div ref={rootRef} className='scroll-mt-[4.5rem]'>
			<div
				className={`flex items-center rounded-xl border-[1px] bg-white transition-colors ${
					hasError ? 'border-red-400' : open ? 'border-[#022c56]' : 'border-slate-200'
				}`}
			>
				<i className='fa-solid fa-magnifying-glass text-slate-400 pl-[1rem]'></i>
				<input
					ref={inputRef}
					type='text'
					role='combobox'
					aria-expanded={open}
					aria-controls={listId}
					aria-autocomplete='list'
					autoComplete='off'
					value={open ? query : (selected?.name ?? '')}
					placeholder={selected && open ? selected.name : 'Search or choose an authority'}
					onFocus={() => {
						setQuery('');
						openList();
					}}
					onChange={(e) => {
						setQuery(e.target.value);
						if (!open) openList();
					}}
					className={`flex-1 min-w-0 bg-transparent outline-none px-[0.75rem] py-[0.75rem] text-base text-slate-900 ${
						selected && !open ? 'font-semibold' : ''
					}`}
				/>
				<button
					type='button'
					onClick={() => (open ? close() : openList())}
					className='self-stretch px-[1rem] text-slate-400'
					aria-label={open ? 'Hide authorities' : 'Show all authorities'}
				>
					<i className={`fa-solid fa-chevron-down text-sm transition-transform ${open ? 'rotate-180' : ''}`}></i>
				</button>
			</div>

			{open && (
				<ul
					id={listId}
					role='listbox'
					className='mt-2 max-h-[40vh] overflow-y-auto overscroll-contain m-0 p-1 list-none rounded-xl border-[1px] border-slate-200 bg-white shadow-lg'
				>
					{filtered.length === 0 && (
						<li className='px-[0.75rem] py-[1rem] text-sm text-slate-500 text-center'>
							No authority matches “{query}”
						</li>
					)}
					{filtered.map((a) => {
						const isSelected = a.id === selectedId;
						return (
							<li key={a.id} role='option' aria-selected={isSelected}>
								<button
									type='button'
									onClick={() => choose(a.id)}
									className={`w-full flex items-center gap-[0.75rem] px-[0.75rem] py-[0.75rem] rounded-lg text-left ${
										isSelected ? 'bg-blue-50' : 'hover:bg-slate-50 active:bg-slate-100'
									}`}
								>
									<span className='flex-1 min-w-0 font-medium text-slate-900'>{a.name}</span>
									{isSelected && <i className='fa-solid fa-check text-[#022c56]'></i>}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default AuthorityPicker;
